import { safeJsonParse } from './safeJsonParse';
import { parseImgsNamesArray, parseImgsSignaturesArray } from './parseSocialLinksFromImgsBlock';

const isAuthorCardsListEntry = (entry) => {
  if (!entry) return false;
  if (entry.eng_name === 'cards_with_authors') return true;

  const tabTag = String(entry.tab_tag || '').toLowerCase();
  return tabTag.includes('cards_with_authors') || tabTag.includes('cards_with_a');
};

const normalizeText = (value) => {
  const text = String(value ?? '').trim();
  if (!text || text.toLowerCase() === 'none') return '';
  return text;
};

const getContentTag = (item) =>
  String(item?.content_tag || item?.cotent_tag || '').trim().toLowerCase();

const getImageFileName = (imgName) => {
  const raw = normalizeText(imgName);
  if (!raw) return '';
  if (raw.includes('\\')) return raw.substring(raw.lastIndexOf('\\') + 1);
  if (raw.includes('/')) return raw.split('/').pop();
  return raw;
};

const buildAuthorCardPath = (item) => {
  const pathPrefix = String(item?.path || '').trim();
  const engName = String(item?.eng_name || '').trim();

  if (!pathPrefix) return '';

  const normalizedPrefix = pathPrefix.startsWith('http') ? pathPrefix : `https://${pathPrefix}`;
  if (!engName) return normalizedPrefix;

  return `${normalizedPrefix}${engName}/`;
};

const normalizeAuthorCardContent = (rawContent) => {
  if (Array.isArray(rawContent)) return rawContent;
  if (rawContent && typeof rawContent === 'object') return rawContent;
  if (typeof rawContent === 'string') return safeJsonParse(rawContent, null);
  return null;
};

const extractBlockContent = (parsedContent) => {
  if (!parsedContent) return null;

  if (Array.isArray(parsedContent)) {
    if (parsedContent.length === 0) return [];

    const hasBlockWrappers = parsedContent.some((item) => Array.isArray(item?.block_content));
    if (!hasBlockWrappers) return parsedContent;

    return parsedContent.flatMap((item) =>
      Array.isArray(item?.block_content) ? item.block_content : [],
    );
  }

  if (Array.isArray(parsedContent?.block_content)) {
    return parsedContent.block_content;
  }

  return null;
};

const parseAuthorCardBlockContent = (blockContent, cardPath) => {
  const card = {
    path: cardPath,
    name: '',
    avatarDescription: '',
    avatarImgName: '',
    avatarImgUrl: '',
    socialImgsNames: [],
    socialImgsSignatures: [],
    filterTag: '',
  };

  if (!Array.isArray(blockContent)) return card;

  blockContent.forEach((item) => {
    const tag = getContentTag(item);

    if (tag === 'name') {
      card.name = normalizeText(item?.content_text);
      return;
    }

    if (tag === 'avatar_and_description') {
      card.avatarDescription = normalizeText(item?.content_text);
      card.avatarImgName = getImageFileName(item?.img_name);
      if (card.avatarImgName && cardPath) {
        card.avatarImgUrl = `${cardPath}${card.avatarImgName}`;
      }
      return;
    }

    if (tag === 'social') {
      card.socialImgsNames = parseImgsNamesArray(item?.imgs_names);
      card.socialImgsSignatures = parseImgsSignaturesArray(item?.imgs_signatures);
      return;
    }

    if (tag === 'ftag_for_filter' || tag === 'tag_for_filter') {
      card.filterTag = normalizeText(item?.content_text);
    }
  });

  return card;
};

const mapAuthorCardFromListItem = (item) => {
  const path = buildAuthorCardPath(item);
  const blockContent = extractBlockContent(normalizeAuthorCardContent(item?.content));

  return parseAuthorCardBlockContent(blockContent, path);
};

export const extractAuthorCardsFromOthers = (others) => {
  if (!Array.isArray(others)) return [];

  const authorListEntries = others.filter(isAuthorCardsListEntry);
  if (authorListEntries.length === 0) return [];

  const rawCards = [];

  authorListEntries.forEach((entry) => {
    const dataSource = Array.isArray(entry?.data) ? entry.data : safeJsonParse(entry?.data, null);
    const contentSource = Array.isArray(entry?.content)
      ? entry.content
      : safeJsonParse(entry?.content, null);

    if (Array.isArray(dataSource)) {
      rawCards.push(...dataSource.filter((item) => item && typeof item === 'object'));
    }

    if (Array.isArray(contentSource)) {
      rawCards.push(...contentSource.filter((item) => item && typeof item === 'object'));
    }
  });

  const seenKeys = new Set();

  return rawCards
    .map(mapAuthorCardFromListItem)
    .filter((card) => card.path || card.name || card.filterTag)
    .filter((card) => {
      const key = card.path || card.filterTag || card.name;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });
};
